import type { ProjectFundUserInfoCreateRequest } from '@/api/api.v2';
import { api, type ProjectFundWellInfo } from '@/api/api.v2';
import { useCallback, useRef, useState, type FC } from 'react';
import { useWellInfo } from '../CreateWellModal';
import type { InputRef, SelectProps } from 'antd';
import { Button, Checkbox, Divider, Input, Select, ColorPicker as ColorPickerAntd, InputNumber } from 'antd';
import type { InputProps } from 'antd/lib';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import type { ProjectFund } from '@/store/project-fund';
import type { RootDispatch, RootState } from '@/components/WellLoggingOperations/types';
import type { ICellRendererParams } from 'ag-grid-community';
import type { DropdownOption, ProjectFundWellInfoWithIndex } from '../types';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import type { Color } from 'antd/es/color-picker';
import { Button as MiButton } from '@/shared/MiButton';
import { ColorIcon } from '../icons/ColorIcon';
import { ADDITIONAL_KEYS_MAP, VALIDATION_KEYS_MAP } from '../constants';

type CustomProps = {
    label: string;
    addInput?: boolean;
    highlightError?: boolean;
    resetValidationError?: () => void;
    customPropKey?: keyof ProjectFundWellInfo;
};

type Key = {
    propKey?: keyof ProjectFundWellInfo;
};

type DoubleKey = {
    propKey1: keyof ProjectFundWellInfo;
    propKey2: keyof ProjectFundWellInfo;
};

export const CustomSelect: FC<SelectProps & CustomProps & Key> = ({
    label,
    propKey,
    addInput,
    options,
    highlightError,
    customPropKey,
    ...props
}) => {
    const { wellInfo, setWellInfo } = useWellInfo();
    const [title, setTitle] = useState('');
    const [validationError, setValidationError] = useState(false);
    const [isValidation, setIsValidation] = useState(false);

    const { activeProjectFond, customOptionsMap } = useSelector((state: RootState) => state.projectFund);
    //@ts-ignore
    const dispatch = useDispatch<RootDispatch>();
    const { addCustomOption } = dispatch.projectFund;

    const customOptions = customOptionsMap[activeProjectFond ?? 0] ?? {};

    const inputRef = useRef<InputRef>(null);

    const additionalOptionsKey = propKey
        ? (VALIDATION_KEYS_MAP[propKey] as keyof Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>)
        : null;

    const addOption = async () => {
        setIsValidation(true);

        if (customOptions && wellInfo.fieldId && additionalOptionsKey && propKey && VALIDATION_KEYS_MAP[propKey]) {
            const { isExist } = await api.map.projectFund.validateFormField({
                table: VALIDATION_KEYS_MAP[propKey] as keyof Omit<ProjectFundUserInfoCreateRequest, 'projectFundId'>,
                value: title,
                fieldId: wellInfo.fieldId,
            });
            let valErr = false;
            if (additionalOptionsKey && customOptions[additionalOptionsKey]) {
                valErr = customOptions[additionalOptionsKey].some(({ value }) => title === value);
            }

            if (isExist || valErr) {
                setValidationError(true);
            } else {
                addCustomOption({ value: title, key: additionalOptionsKey, fieldId: wellInfo.fieldId });
                setTitle('');
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 0);
            }
        }
        setIsValidation(false);
    };

    return (
        <div className="mt-2">
            <span className="text-xs">{label}</span>
            <Select
                placeholder="Выберите"
                className="w-full !mt-1"
                {...props}
                value={
                    propKey
                        ? customPropKey
                            ? (wellInfo[customPropKey] ?? wellInfo[propKey])
                            : wellInfo[propKey]
                        : props.value
                }
                status={
                    propKey &&
                    highlightError &&
                    (wellInfo[propKey] === null || wellInfo[propKey] === undefined) &&
                    (customPropKey ? wellInfo[customPropKey] === null : true)
                        ? 'error'
                        : undefined
                }
                dropdownRender={menu => {
                    return (
                        <>
                            {menu}
                            {addInput ? (
                                <>
                                    <Divider style={{ margin: '0 0 8px 0' }} />
                                    <div className="flex mx-1 mb-2">
                                        <Input
                                            ref={inputRef}
                                            size="small"
                                            status={validationError ? 'error' : undefined}
                                            placeholder="Введите название"
                                            value={title}
                                            onChange={e => setTitle(e.target.value)}
                                            onKeyDown={e => e.stopPropagation()}
                                        />
                                        <Button
                                            loading={isValidation}
                                            size="small"
                                            type="text"
                                            icon={<PlusOutlined />}
                                            onClick={addOption}
                                        />
                                    </div>
                                </>
                            ) : null}
                        </>
                    );
                }}
                onChange={
                    propKey
                        ? (v, option) => {
                              setWellInfo(prev =>
                                  customPropKey
                                      ? //@ts-ignore
                                        option?.children === v
                                          ? { ...prev, [customPropKey]: v, [propKey]: null }
                                          : { ...prev, [propKey]: v, [customPropKey]: null }
                                      : { ...prev, [propKey]: v },
                              );
                          }
                        : props.onChange
                }
            >
                {customOptions &&
                    additionalOptionsKey &&
                    customOptions[additionalOptionsKey] &&
                    customOptions[additionalOptionsKey].map(({ value }) => (
                        <Select.Option key={value} className="bg-gray-200 text-xs" value={value}>
                            {value}
                        </Select.Option>
                    ))}
                {(options ?? [])
                    .filter(({ value }) => value)
                    .map(({ label, value }) => (
                        <Select.Option key={value} className="text-xs" value={value}>
                            {label}
                        </Select.Option>
                    ))}
            </Select>
        </div>
    );
};

export const CustomInput: FC<InputProps & CustomProps & Key> = ({ label, propKey, resetValidationError, ...props }) => {
    const { wellInfo, setWellInfo } = useWellInfo();
    return (
        <div className="mt-2">
            <span className="text-xs">{label}</span>
            <Input
                className="w-full !mt-1"
                value={propKey ? (wellInfo[propKey] ?? 0) : props.value}
                onChange={
                    propKey
                        ? e => {
                              if (resetValidationError) {
                                  resetValidationError();
                              }
                              setWellInfo(prev => ({ ...prev, [propKey]: e.target.value }));
                          }
                        : props.onChange
                }
                {...props}
            />
        </div>
    );
};

export const CustomDoubleInput: FC<{ input1?: InputProps; input2?: InputProps } & CustomProps & DoubleKey> = ({
    label,
    propKey1,
    propKey2,
    highlightError,
    ...props
}) => {
    const { wellInfo, setWellInfo } = useWellInfo();
    return (
        <div className="mt-2">
            <span className="text-xs">{label}</span>
            <div className="flex !mt-1">
                <InputNumber
                    prefix={<span className="text-xs text-gray-700">x</span>}
                    className="!w-full"
                    style={{ marginRight: '8px' }}
                    value={wellInfo[propKey1]}
                    status={
                        (highlightError && wellInfo[propKey1] === null) || Number.isNaN(wellInfo[propKey1])
                            ? 'error'
                            : undefined
                    }
                    onChange={v => setWellInfo(prev => ({ ...prev, [propKey1]: Number(v) ?? null }))}
                    {...props}
                />
                <InputNumber
                    prefix={<span className="text-xs text-gray-700">y</span>}
                    className="!w-full"
                    value={wellInfo[propKey2]}
                    status={
                        (highlightError && wellInfo[propKey2] === null) || Number.isNaN(wellInfo[propKey2])
                            ? 'error'
                            : undefined
                    }
                    onChange={v => setWellInfo(prev => ({ ...prev, [propKey2]: Number(v) ?? null }))}
                    {...props}
                />
            </div>
        </div>
    );
};

type CheckboxCellProps = {
    data: ProjectFund;
    onToggleFond: (id: number) => void;
    selectedProjectFonds: number[];
};

export const CheckboxCell = (params: CheckboxCellProps) => {
    const { data, onToggleFond, selectedProjectFonds } = params;
    const id = data?.id;

    if (id == null) return null;

    const checked = selectedProjectFonds.includes(id);

    return (
        <Checkbox
            checked={checked}
            onChange={event => {
                event.stopPropagation();
                onToggleFond(id);
            }}
        />
    );
};

export const ColorPicker = (params: { data: ProjectFund }) => {
    const { id, color } = params.data ?? {};
    const { isColorLoading, mapColors } = useSelector((state: RootState) => state.projectFund);
    const dispatch = useDispatch();
    const { changeColor } = dispatch.projectFund;

    const [isOpen, setIsOpen] = useState(false);
    const [currentColor, setCurrentColor] = useState<string>(color);

    const onCancel = () => setIsOpen(false);

    const handleOpenChange = (open: boolean) => {
        if (!open && isOpen) {
            changeColor({ projectFundId: id, color: currentColor });
        }
        setIsOpen(open);
    };

    const handleChange = (color: Color) => {
        setCurrentColor(color.toHexString());
    };

    return isColorLoading.includes(id) ? (
        <LoadingOutlined />
    ) : (
        <ColorPickerAntd
            open={isOpen}
            value={currentColor}
            panelRender={panel => (
                <div>
                    {panel}

                    {mapColors.length > 0 ? (
                        <div>
                            <Divider className="!m-2 !mx-0" />
                            <span className="text-xs">Цвета на карте</span>
                            <div className="mt-2">
                                {mapColors.map(color => (
                                    <Button
                                        key={color}
                                        type="text"
                                        className="!p-0 !w-4 !h-4 mr-2"
                                        style={{ backgroundColor: color }}
                                        size="small"
                                        onClick={() => setCurrentColor(color)}
                                    ></Button>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    <Divider className="!m-2 !mx-0" />
                    <div className="flex flex-row-reverse">
                        <MiButton type="primary" onClick={() => handleOpenChange(false)}>
                            Применить
                        </MiButton>
                        <MiButton className="mr-2" onClick={onCancel}>
                            Отмена
                        </MiButton>
                    </div>
                </div>
            )}
            size="small"
            onChange={handleChange}
            onOpenChange={handleOpenChange}
        >
            <Button className="!p-0" type="text" size="small" style={{ color }}>
                <ColorIcon />
            </Button>
        </ColorPickerAntd>
    );
};

type InputCellRendererProps = ICellRendererParams<ProjectFundWellInfoWithIndex, string | number> & {
    updateWellField: <K extends keyof ProjectFundWellInfoWithIndex>(
        index: number,
        key: K,
        value: ProjectFundWellInfoWithIndex[K],
    ) => void;
};

export const InputCellRenderer: FC<InputCellRendererProps> = ({ value, colDef, data, updateWellField }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (colDef?.field && data?.index !== undefined) {
            updateWellField(data.index, colDef.field as keyof ProjectFundWellInfoWithIndex, e.target.value);
        }
    };

    return <Input size="small" variant="borderless" value={value ?? undefined} onChange={handleChange} />;
};

type SelectCellRendererProps = ICellRendererParams<ProjectFundWellInfoWithIndex, number | null> & {
    options: DropdownOption[];
    updateWellField: <K extends keyof ProjectFundWellInfoWithIndex>(
        index: number,
        key: K,
        value: ProjectFundWellInfoWithIndex[K],
        excludedKey?: K,
    ) => void;
};

export const SelectCellRenderer: FC<SelectCellRendererProps> = props => {
    const { value, getOptions, updateWellField, data } = props;

    const key = data?.fieldId ?? data?.explorationZoneId;

    const handleChange = useCallback(
        (newValue: number, option) => {
            if (props.colDef?.field && props.colDef?.field !== 'index') {
                const propKey = props.colDef?.field as keyof ProjectFundWellInfoWithIndex;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const additionalKey = ADDITIONAL_KEYS_MAP[propKey];
                if (additionalKey) {
                    const isCustom = option.label?.props?.children === newValue;
                    if (isCustom) {
                        updateWellField(
                            props.data?.index as number,
                            additionalKey,
                            newValue,
                            props.colDef?.field as keyof ProjectFundWellInfoWithIndex,
                        );
                    } else {
                        updateWellField(
                            props.data?.index as number,
                            props.colDef?.field as keyof ProjectFundWellInfoWithIndex,
                            newValue,
                            additionalKey,
                        );
                    }
                } else {
                    updateWellField(
                        props.data?.index as number,
                        props.colDef?.field as keyof ProjectFundWellInfoWithIndex,
                        newValue,
                    );
                }
            }
        },
        [props.colDef?.field, props.data?.index, updateWellField],
    );

    return (
        <Select
            style={{ width: '100%' }}
            options={getOptions(key).map(({ value, label }) => ({
                value,
                label: <span className="text-xs">{label}</span>,
            }))}
            value={value ?? null}
            onChange={handleChange}
            variant="borderless"
            size="small"
            suffixIcon={null}
        />
    );
};
